import React, { useEffect, useState } from 'react'
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import { createAccount, updateAccount } from '../../actions/accountActions';
import useAuthUser from '../../hooks/useAuthUser';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: '#1976D2',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const defaultState = { name: '', billingAddress: '', billingCity: '', billingState: '', billingZip: '', taxIdNumber: '', term: '', phone: '', email: '', userId: [] }

const inputStyle = {
  display: "block",
  padding: "1.1rem 0.75rem",
  width: "100%",
  fontSize: "0.9rem",
  lineHeight: 1.25,
  color: "#55595c",
  backgroundColor: "#fff",
  backgroundImage: "none",
  backgroundClip: "padding-box",
  borderTop: "0",
  borderRight: "0",
  borderBottom: "1px solid #eee",
  borderLeft: "0",
  borderRadius: "3px",
  transition: "all 0.25s cubic-bezier(0.4, 0, 1, 1)"
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC'
]

const AddAccount = ({ setOpen, open, currentId, setCurrentId }) => {
  const authData = useAuthUser()
  const dispatch = useDispatch()
  const account = useSelector((state) => currentId ? state.accounts.accounts.find((a) => a._id === currentId) : null)
  const [accountData, setAccountData] = useState(defaultState)
  // eslint-disable-next-line
  const [openSnackbar, closeSnackbar] = useSnackbar()

  useEffect(() => {
    if (account) {
      setAccountData(account)
    }
  }, [account])

  useEffect(() => {
    const userId = authData?.result?._id || authData?.result?.googleId
    if (userId) {
      setAccountData((prev) => ({ ...prev, userId: [userId] }))
    }
  }, [authData])

  const clear = () => {
    setCurrentId(null)
    setAccountData(defaultState)
  }

  const handleClose = () => {
    setOpen(false);
    clear();
  };

  const handleSubmit = (e) => {
    e.preventDefault()
    const userId = authData?.result?._id || authData?.result?.googleId
    const payload = {
      ...accountData,
      userId: accountData.userId && accountData.userId.length ? accountData.userId : (userId ? [userId] : []),
    }
    if (currentId) {
      dispatch(updateAccount(currentId, payload, openSnackbar))
    } else {
      dispatch(createAccount(payload, openSnackbar))
    }
    handleClose()
  }

  return (
    <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open} fullWidth>
      <DialogTitle id="customized-dialog-title" onClose={handleClose} style={{ paddingLeft: '20px', color: 'white' }}>
        {currentId ? 'Edit Account' : 'Add Account'}
      </DialogTitle>
      <DialogContent dividers>
        <div className="customInputs">
          <input
            placeholder="Name"
            style={inputStyle}
            name='name'
            type='text'
            onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
            value={accountData.name}
          />
          <input
            placeholder="Billing Address"
            style={inputStyle}
            name='billingAddress'
            type='text'
            onChange={(e) => setAccountData({ ...accountData, billingAddress: e.target.value })}
            value={accountData.billingAddress}
          />
          <input
            placeholder="City"
            style={inputStyle}
            name='billingCity'
            type='text'
            onChange={(e) => setAccountData({ ...accountData, billingCity: e.target.value })}
            value={accountData.billingCity}
          />
          <select
            style={inputStyle}
            name='billingState'
            onChange={(e) => setAccountData({ ...accountData, billingState: e.target.value })}
            value={accountData.billingState}
          >
            <option value="" disabled>State</option>
            {US_STATES.map((abbr) => (
              <option key={abbr} value={abbr}>{abbr}</option>
            ))}
          </select>
          <input
            placeholder="Zip"
            style={inputStyle}
            name='billingZip'
            type='text'
            onChange={(e) => setAccountData({ ...accountData, billingZip: e.target.value })}
            value={accountData.billingZip}
          />
          <input
            placeholder="Tax ID Number"
            style={inputStyle}
            name='taxIdNumber'
            type='text'
            onChange={(e) => setAccountData({ ...accountData, taxIdNumber: e.target.value })}
            value={accountData.taxIdNumber}
          />
          <input
            placeholder="Term (Days)"
            style={inputStyle}
            name='term'
            type='number'
            onChange={(e) => setAccountData({ ...accountData, term: e.target.value })}
            value={accountData.term}
          />
          <input
            placeholder="Primary Phone Number"
            style={inputStyle}
            name='phone'
            type='text'
            onChange={(e) => setAccountData({ ...accountData, phone: e.target.value })}
            value={accountData.phone}
          />
          <input
            placeholder="Email"
            style={inputStyle}
            name='email'
            type='email'
            onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
            value={accountData.email}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained" color="primary" style={{ marginRight: '16px' }}>
          Save Account
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddAccount
